import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, SlidersHorizontal, MapPin, X, Users, UserCheck, UserX, Upload, Trash2, Layers, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { useDebouncedValue } from "@/lib/hooks";
import LeadCard from "@/components/LeadCard";
import LeadDialog from "@/components/LeadDialog";
import ImportLeadsDialog from "@/components/ImportLeadsDialog";
import OpportunityDialog from "@/components/OpportunityDialog";
import { todayStr } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, STATUS_ORDER } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ASSIGNMENT_TABS = [
  { key: "all",        label: "All Leads",  Icon: Users },
  { key: "unassigned", label: "Unassigned", Icon: UserX },
  { key: "assigned",   label: "Assigned",   Icon: UserCheck },
];

export default function LeadsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [location, setLocation] = useState(searchParams.get("location") || "all");
  const [temperature, setTemperature] = useState(searchParams.get("temperature") || "all");
  const [assignment, setAssignment] = useState(searchParams.get("assignment") || "all");
  const [assignedTo, setAssignedTo] = useState(searchParams.get("assigned_to") || "");
  const [showFilters, setShowFilters] = useState(false);
  const [leadType, setLeadType] = useState(searchParams.get("type") === "live" ? "live" : "bulk");
  const [oppLead, setOppLead] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // multi-select
  const [selected, setSelected] = useState(() => new Set());
  const selectionMode = selected.size > 0;
  const [bulkAssignee, setBulkAssignee] = useState("");

  // Keep URL in sync so filters are shareable / back-nav friendly
  useEffect(() => {
    const params = {};
    if (leadType === "live") params.type = "live";
    if (status !== "all") params.status = status;
    if (location !== "all") params.location = location;
    if (temperature !== "all") params.temperature = temperature;
    if (assignment !== "all") params.assignment = assignment;
    if (assignedTo) params.assigned_to = assignedTo;
    setSearchParams(params, { replace: true });
  }, [leadType, status, location, temperature, assignment, assignedTo, setSearchParams]);

  // React to URL changes triggered externally (Dashboard/Reports links)
  useEffect(() => {
    const s = searchParams.get("status") || "all";
    const l = searchParams.get("location") || "all";
    const t = searchParams.get("temperature") || "all";
    const a = searchParams.get("assignment") || "all";
    const at = searchParams.get("assigned_to") || "";
    setLeadType(searchParams.get("type") === "live" ? "live" : "bulk");
    setStatus(s);
    setLocation(l);
    setTemperature(t);
    setAssignment(a);
    setAssignedTo(at);
  }, [searchParams.toString()]);

  const qc = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();

  const usersQ = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: user?.role === "admin",
    staleTime: 5 * 60_000,
  });

  const locsQ = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/leads/locations")).data.items,
    staleTime: 5 * 60_000,
  });

  const kpisQ = useQuery({
    queryKey: ["dashboard", "kpis"],
    queryFn: async () => (await api.get("/dashboard/kpis", { params: { today: todayStr() } })).data,
    staleTime: 60_000,
  });

  const debouncedSearch = useDebouncedValue(search, 300);

  const leadsQ = useQuery({
    queryKey: ["leads", { leadType, search: debouncedSearch, status, location, temperature, assignment, assignedTo }],
    queryFn: async () => {
      const params = { limit: 300, lead_type: leadType };
      if (debouncedSearch) params.search = debouncedSearch;
      if (status !== "all") params.status = status;
      if (location !== "all") params.location = location;
      if (temperature !== "all") params.temperature = temperature;
      if (assignment !== "all") params.assignment = assignment;
      if (assignedTo) params.assigned_to = assignedTo;
      const { data } = await api.get("/leads", { params });
      return data;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const bulkMut = useMutation({
    mutationFn: async () => {
      await api.post("/leads/bulk-assign", {
        lead_ids: Array.from(selected),
        assigned_to: bulkAssignee || null,
      });
    },
    onSuccess: () => {
      toast.success(`Updated ${selected.size} lead(s)`);
      setSelected(new Set());
      setBulkAssignee("");
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: () => toast.error("Bulk update failed"),
  });

  const bulkDeleteMut = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/leads/bulk-delete", {
        lead_ids: Array.from(selected),
      });
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Deleted ${data.deleted} lead(s)`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Bulk delete failed"),
  });

  const items = leadsQ.data?.items || [];
  const isAdmin = user?.role === "admin";

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (l) => {
    // Navigate to full-screen detail page for existing leads
    navigate(`/leads/${l.id}`);
  };

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const locationChips = useMemo(() => {
    const arr = ["all", ...(locsQ.data || [])];
    return arr;
  }, [locsQ.data]);

  return (
    <div className="space-y-4" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-navy lg:text-3xl">{leadType === "live" ? "Live Leads" : "Bulk Leads"}</h2>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <ImportLeadsDialog
              trigger={
                <Button
                  data-testid="lead-import-btn"
                  variant="outline"
                  className="h-11 rounded-full border-slate-200 px-4 text-sm font-semibold text-navy"
                >
                  <Upload className="h-4 w-4" /> Import CSV
                </Button>
              }
            />
          )}
          <Button
            data-testid="lead-add-btn"
            onClick={openNew}
            className="h-11 w-11 rounded-full bg-navy p-0 hover:brightness-110"
            aria-label="Add lead"
          >
            <Plus className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Bulk vs Live split */}
      <div className="grid grid-cols-2 gap-1 rounded-2xl bg-slate-100 p-1" data-testid="lead-type-tabs">
        {[
          { key: "bulk", label: "Bulk Leads", Icon: Layers, count: kpisQ.data?.total_bulk },
          { key: "live", label: "Live Leads", Icon: Radio, count: kpisQ.data?.total_live },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`lead-type-tab-${t.key}`}
            data-active={leadType === t.key}
            onClick={() => { setLeadType(t.key); setSelected(new Set()); }}
            className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
              leadType === t.key ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <t.Icon className={`h-4 w-4 ${t.key === "live" && leadType === "live" ? "text-rose-500" : ""}`} />
            {t.label}
            {t.count != null && (
              <span className="rounded-full bg-slate-200/70 px-2 py-0.5 text-[11px] font-bold text-slate-600">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="lead-search"
          placeholder="Search by name, phone, area..."
          className="h-12 rounded-xl border-slate-200 bg-white pl-10 pr-14 text-[15px] shadow-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          data-testid="lead-filter-toggle"
          onClick={() => setShowFilters((v) => !v)}
          className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-slate-500 hover:bg-slate-100"
          aria-label="Toggle filters"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </button>
      </div>

      {/* Assignment tabs (admin only) */}
      {isAdmin && (
        <div className="flex gap-2">
          {ASSIGNMENT_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`assign-tab-${t.key}`}
              data-active={assignment === t.key}
              className="chip"
              onClick={() => setAssignment(t.key)}
            >
              <t.Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Status chip row */}
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 no-scrollbar">
        <button
          data-testid="status-chip-all"
          data-active={status === "all"}
          className="chip"
          onClick={() => setStatus("all")}
        >
          All
        </button>
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            data-testid={`status-chip-${s}`}
            data-active={status === s}
            className="chip"
            onClick={() => setStatus(s)}
          >
            <span className={`status-dot status-${s}`} />
            {STATUS_META[s].label}
          </button>
        ))}
      </div>

      {/* Location chip row */}
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 no-scrollbar">
        {locationChips.map((l) => (
          <button
            key={l}
            data-testid={`location-chip-${l}`}
            data-active={location === l}
            className="chip chip-gold"
            onClick={() => setLocation(l)}
          >
            <MapPin className="h-3.5 w-3.5" />
            {l === "all" ? "All Locations" : l}
          </button>
        ))}
      </div>

      {/* Team member chip row (admin only — agents already see only their own) */}
      {isAdmin && (
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 no-scrollbar" data-testid="user-chip-row">
          <button
            data-testid="user-chip-all"
            data-active={!assignedTo}
            className="chip"
            onClick={() => setAssignedTo("")}
          >
            <Users className="h-3.5 w-3.5" />
            All Owners
          </button>
          {(usersQ.data || []).map((u) => (
            <button
              key={u.id}
              data-testid={`user-chip-${u.id}`}
              data-active={assignedTo === u.id}
              className="chip"
              onClick={() => setAssignedTo(u.id)}
            >
              <span
                aria-hidden
                className="grid h-5 w-5 place-items-center rounded-full bg-[hsl(var(--brand-navy))] text-[10px] font-semibold text-white"
              >
                {u.initials || u.name[0]}
              </span>
              {u.name.split(" ")[0]}
            </button>
          ))}
        </div>
      )}

      {/* Optional advanced filters */}
      {showFilters && (
        <div className="soft-card grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-medium text-slate-500">Temperature</div>
            <Select value={temperature} onValueChange={setTemperature}>
              <SelectTrigger data-testid="filter-temperature"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="hot">Hot</SelectItem>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="cold">Cold</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Active team-member filter chip (from Reports → Calls per Employee) */}
      {assignedTo && (() => {
        const target = (usersQ.data || []).find((u) => u.id === assignedTo);
        if (!target) return null;
        return (
          <div className="flex items-center gap-2" data-testid="assigned-filter-chip">
            <span className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--brand-navy))] px-3 py-1.5 text-xs font-semibold text-white">
              Owner: {target.name}
              <button
                data-testid="clear-assigned-filter"
                onClick={() => setAssignedTo("")}
                aria-label="Clear owner filter"
                className="grid h-4 w-4 place-items-center rounded-full bg-white/20 hover:bg-white/30"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        );
      })()}

      {/* Bulk assign bar */}
      {selectionMode && (
        <div className="soft-card sticky top-16 z-10 flex flex-wrap items-center gap-2 border-l-4 border-[hsl(var(--brand-navy))] p-3 anim-fade-up" data-testid="bulk-bar">
          <span className="text-sm font-semibold text-navy">{selected.size} selected</span>
          <button
            data-testid="bulk-select-all-btn"
            onClick={() => {
              if (selected.size >= items.length) {
                setSelected(new Set());
              } else {
                setSelected(new Set(items.map((l) => l.id)));
              }
            }}
            className="text-[12px] font-semibold text-navy hover:underline"
          >
            {selected.size >= items.length ? "Deselect all" : `Select all ${items.length}`}
          </button>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <>
                <Select value={bulkAssignee || "__none"} onValueChange={(v) => setBulkAssignee(v === "__none" ? "" : v)}>
                  <SelectTrigger data-testid="bulk-assignee-select" className="h-9 w-48">
                    <SelectValue placeholder="Assign to…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Unassign</SelectItem>
                    {(usersQ.data || [])
                      .filter((u) => !u.is_off)
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="bulk-apply-btn"
                  className="bg-navy hover:brightness-110"
                  disabled={bulkMut.isPending}
                  onClick={() => bulkMut.mutate()}
                >
                  Apply
                </Button>
              </>
            )}
            {isAdmin && (
              <Button
                data-testid="bulk-delete-btn"
                variant="outline"
                className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                disabled={bulkDeleteMut.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `Delete ${selected.size} lead(s)? This action cannot be undone.`
                    )
                  ) {
                    bulkDeleteMut.mutate();
                  }
                }}
              >
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
            )}
            <button
              data-testid="bulk-clear-btn"
              onClick={() => setSelected(new Set())}
              className="grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100"
              aria-label="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Info banner + Select all */}
      {!selectionMode && items.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-800" data-testid="bulk-hint">
          <span>ⓘ Right-click (long-press on mobile) or hit Select all to pick leads for bulk actions.</span>
          <button
            data-testid="select-all-btn"
            onClick={() => setSelected(new Set(items.map((l) => l.id)))}
            className="rounded-full bg-[hsl(var(--brand-navy))] px-3 py-1 text-[11px] font-semibold text-white hover:brightness-110"
          >
            Select all {items.length}
          </button>
        </div>
      )}

      {/* Leads grid */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {leadsQ.isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="soft-card h-40 animate-pulse bg-slate-50" />
          ))}
        {!leadsQ.isLoading && items.length === 0 && (
          <div className="col-span-full soft-card p-8 text-center text-slate-500">
            {leadType === "live" && !kpisQ.data?.total_live
              ? "No live leads yet — new Facebook Lead Ad submissions will appear here automatically."
              : "No leads match your filters."}
          </div>
        )}
        {items.map((l) => (
          <LeadCard
            key={l.id}
            lead={l}
            selected={selected.has(l.id)}
            selectionMode={selectionMode}
            onOpen={openEdit}
            onSelectToggle={toggle}
            onCreateOpp={setOppLead}
          />
        ))}
      </div>

      <OpportunityDialog open={Boolean(oppLead)} onOpenChange={(v) => !v && setOppLead(null)} lead={oppLead} />

      <LeadDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) setEditing(null);
        }}
        lead={editing}
        users={usersQ.data || []}
      />
    </div>
  );
}
